import { AlertTriangle, CheckCircle2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import type { ServiceKeyFacts, ServiceSummary } from '@repo/shared-types';

const ENTRY_TYPE_LABELS: Record<string, string> = {
  service: 'Service',
  mot: 'MOT',
  repair: 'Repair',
  other: 'Other',
};

const formatMileage = (miles: number | null) =>
  miles === null ? '—' : `${miles.toLocaleString('en-GB')} mi`;

const formatStamped = (stamped: boolean | null) => {
  if (stamped === null) return '—';
  return stamped ? 'Yes' : 'No';
};

interface ServiceFactsViewProps {
  summary: ServiceSummary;
  /** Called with a document id when a source file is clicked. */
  onOpenFile?: (documentId: string) => void;
  /** Hide the summary text, e.g. while it is being edited above. */
  hideSummaryText?: boolean;
}

/** Summary text, headline figures, every entry with its source file, gaps and unreadable parts. */
export const ServiceFactsView = ({
  summary,
  onOpenFile,
  hideSummaryText = false,
}: ServiceFactsViewProps) => {
  const facts: ServiceKeyFacts | null = summary.key_facts;

  return (
    <div className="space-y-4">
      {summary.summary && !hideSummaryText && (
        <p className="text-sm leading-relaxed whitespace-pre-line">{summary.summary}</p>
      )}

      {facts && (
        <>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <Figure label="Services" value={String(facts.total_services)} />
            <Figure
              label="Last service"
              value={
                facts.last_service
                  ? `${facts.last_service.date} · ${formatMileage(facts.last_service.mileage)}`
                  : '—'
              }
            />
            <Figure
              label="Vehicle"
              value={[facts.make, facts.model].filter(Boolean).join(' ') || '—'}
            />
            <Figure
              label="Registrations in files"
              value={facts.registrations_found.join(', ') || '—'}
            />
          </div>

          {facts.gaps.length > 0 && (
            <div className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950">
              <p className="flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-4 w-4" /> Gaps in the history
              </p>
              {facts.gaps.map((gap) => (
                <p key={`${gap.from_date}-${gap.to_date}`} className="text-muted-foreground">
                  {gap.from_date} → {gap.to_date}: {gap.months} months
                  {gap.miles !== null && `, ${gap.miles.toLocaleString('en-GB')} miles`}
                </p>
              ))}
            </div>
          )}
          {facts.gaps.length === 0 && facts.total_services > 1 && (
            <p className="flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" /> No gaps over 12 months or 12,000 miles
            </p>
          )}

          {facts.services.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Mileage</TableHead>
                  <TableHead>Garage</TableHead>
                  <TableHead>Work done</TableHead>
                  <TableHead>Stamped</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {facts.services.map((entry, index) => {
                  const documentId = entry.source_document_id;
                  const source = `${entry.source_file}${entry.source_page ? ` (p. ${entry.source_page})` : ''}`;
                  return (
                    <TableRow key={`${entry.source_file}-${index}`}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {entry.date ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={entry.entry_type === 'service' ? 'default' : 'secondary'}>
                          {ENTRY_TYPE_LABELS[entry.entry_type] ?? entry.entry_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {formatMileage(entry.mileage)}
                      </TableCell>
                      <TableCell className="text-xs">{entry.garage ?? '—'}</TableCell>
                      <TableCell className="text-xs">{entry.work_done}</TableCell>
                      <TableCell className="text-xs">{formatStamped(entry.stamped)}</TableCell>
                      <TableCell className="text-xs">
                        {documentId && onOpenFile ? (
                          <button
                            type="button"
                            className="text-primary text-left underline-offset-2 hover:underline"
                            onClick={() => onOpenFile(documentId)}
                          >
                            {source}
                          </button>
                        ) : (
                          source
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-sm">No service entries were found.</p>
          )}

          {facts.unreadable.length > 0 && (
            <div className="space-y-1 text-sm">
              <p className="font-medium">Could not be read</p>
              {facts.unreadable.map((item, index) => (
                <p key={`${item.source_file}-${index}`} className="text-muted-foreground">
                  {item.source_file}: {item.description}
                </p>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Figure = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-md border p-2">
    <p className="text-muted-foreground text-xs">{label}</p>
    <p className="font-medium break-words">{value}</p>
  </div>
);
